import type React from "react";
import { useId } from "react";
import { twJoin } from "tailwind-merge";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { twResolve } from "@/helpers/tw-resolve";

import { ChartPatterns } from "./ChartPatterns";

type ChartLegendItem = {
  label: string | React.ReactNode;
  tooltip?: string;
  color: string;
  pattern?: "solid" | "striped";
};

type ChartLegendProps = {
  items: ChartLegendItem[];
};

export function ChartLegend({ items }: ChartLegendProps): React.ReactNode {
  const idPatternStripes = useId();

  return (
    <div
      className={twResolve(
        "mx-auto flex w-fit max-w-full flex-col flex-wrap items-start gap-x-4 gap-y-2 sm:flex-row",
        items.length > 3 && "sm:grid sm:grid-cols-2",
      )}
    >
      {items.map((item) => {
        const isIndicatorSmall = items.length > 2;

        return (
          <Tooltip key={item.color + "-" + item.pattern} tooltip={item.tooltip}>
            <div className="flex items-center gap-2">
              {item.pattern === "striped" && (
                <div className={twJoin("shrink-0 overflow-hidden rounded-sm", isIndicatorSmall ? "size-3" : "size-4")}>
                  <svg>
                    <ChartPatterns.Stripes id={`${idPatternStripes}{-${item.color}`} color={item.color} />
                    <rect width="100%" height="100%" fill={`url(#${idPatternStripes}{-${item.color})`} />
                  </svg>
                </div>
              )}
              {item.pattern !== "striped" && (
                <div
                  className={twJoin("shrink-0 rounded-sm", isIndicatorSmall ? "size-3" : "size-4")}
                  style={{ backgroundColor: item.color }}
                />
              )}

              <span
                className={twJoin(
                  "whitespace-pre-line text-left text-black",
                  items.length > 2 ? "text-overline" : "text-caption",
                )}
              >
                {item.label}
              </span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}
